import { useState, useEffect, useLayoutEffect } from 'react';
import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Header } from './common'

import { theme } from '../utils/theme';

import { BasicFlex, Flex, FlexLine } from '../utils/Common';

import { getToday, monthsObj, calculateMonthAndYear, calculateDate, getDateParts, calculateWeeks, constructDateFromObj } from '../components/Calendar/helpers'

import { Button, Select, MenuItem, InputLabel, FormControl } from '@mui/material';

import Month from '../components/Calendar/Month';
import TimelineComponent from '../components/Calendar/Timeline';
import DayComponent from '../components/Calendar/Day';
import WeekComponent from '../components/Calendar/Week';
import ThreeDayComponent from '../components/Calendar/ThreeDay';
import ListView from '../components/Calendar/List';

import EditSchedule from '../components/Schedule/EditSchedule';

import { CalendarIcon, RightArrow, LeftArrow } from '../images'

import { useNavigate } from 'react-router-dom';

import FormDialog from '../utils/utilities/Dialog';

import useIsMobile from '../hooks/useIsMobile';

import { useQuery } from '@tanstack/react-query';
import { getStylists } from '../components/Stylists/apiService';
import useStylistsStore from '../store/stylists';
import useLocalStylistsStore from '../store/stylists/local';

import { getSettingByResource } from '../components/Settings/apiService';

import { NewEventProvider, useNewEvent } from '../contexts/NewEvent';

import useAuth from '../hooks/useAuth';

import { useCalendarContext, CalendarContext } from '../contexts/Calendar';

import { Location } from '@hebcal/core';

import { locations } from '../utils/util';

import useQueryParams from '../hooks/useQueryParams';

import SelectDate from '../components/Calendar/Components/SelectDate';

import FollowUpSnackbar from '../store/repair_follow_up/Snackbar';

import { getStylistView } from '../components/Calendar/apiService';

import SelectStylistLocal from "../components/Calendar/Components/SelectStylistLocal";


const Container = styled.div`
`

const Tag = styled(FlexLine)`
  border-radius: 5px;
  background-color: ${props => props.theme.colors.primaryFaint};
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  width: 12rem;
  height: 4.5rem;
  gap: .8rem;

  svg {
    width: 25px;
    height: 25px;
  }
`

const ActionContainer = styled(BasicFlex)`
  /* width: 50%; */
  width: auto;
  max-width: 85%;
  justify-content: flex-end;
  gap: 1.5rem;

  @media ${props => props.theme.breaks.down('lg')} {
    width: 100%;
    max-width: none;
  }

  @media ${props => props.theme.breaks.down('md')} { 
    max-width: none;
    width: 100%;
    gap: 1rem;
    padding: .1rem .3rem;
  }
`

const SelectDateContainer = styled(BasicFlex)`
  align-items: stretch;
  gap: 1rem;

  @media ${props => props.theme.breaks.down('md')} { 
    gap: 1rem;
  }
`;

const DateSelector = styled(BasicFlex)`
  background-color: ${props => props.theme.colors.secondaryFaint};
  border-radius: 5px;
  height: 4.5rem;
  padding: .3rem;
  /* max-width: 23rem; */

  @media ${props => props.theme.breaks.down('md')} {
    max-width: none;
    width: auto;
    height: auto;
    padding: 0;
    flex-grow: 1;
  }
`

const Arrow = styled(BasicFlex)`
  background-color: ${props => props.theme.colors.secondary};
  height: 4rem;
  width: 3.5rem;
  border-radius: 5px;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.secondaryDarken};
  }

  @media ${props => props.theme.breaks.down('md')} {
    height: 100%;
    padding: .3rem 0;
    /* width: 5rem; */
  }
`

const DateText = styled.span`
  display: block;
  color: ${props => props.theme.colors.secondary};
  flex-grow: 1;
  text-align: center;
  padding: 0 2rem;
  /* min-width: fit-content; */

  @media ${props => props.theme.breaks.down('md')} {
    font-size: 1.2rem;
  }
`

const View = styled(BasicFlex)`
  border-radius: 10px;
  border: 1px solid #2C2D38;
  height: 4.5rem;
  /* max-width: 21rem; */
  gap: 1rem;
  padding: .2rem;
  min-width: fit-content;

  @media ${props => props.theme.breaks.down('md')} {
    height: auto;
    border-radius: 3px;
    padding: .1rem;
  }
`

const ViewBlock = styled(BasicFlex)`
  font-weight: 500;
  color: ${props => props.$active ? props.theme.colors.white : props.theme.colors.text};
  background-color: ${props => props.$active ? props.theme.colors.primary : "transparent"};
  height: 4rem;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  padding: 0 1rem;
  cursor: pointer;
  min-width: 10rem;

  @media ${props => props.theme.breaks.down('md')} {
    height: auto;
    border-radius: 3px;
    padding: .4rem 1rem;
    font-size: 1.2rem;
  }
`

const CalendarProvider = () => {
  return (
    <NewEventProvider>
      <Calendar />
    </NewEventProvider>
  )
}

const mergeStylistList = (ol, nl) => {
  if (!ol?.length) return nl;
  return ol.map(o => {
    const n = nl.find(n => n.stylist_id === o.stylist_id);
    return {
      ...o,
      checked: n && n.checked ? true : false
    }
  })
}

const Calendar = () => {
  let today = getToday()

  const { event, setEvent, setOpenDialog, openDialog, createNewEvent, editEvent } = useNewEvent()

  const [calOptions, setCalOptions] = useState({})

  const { isMobile, isTablet } = useIsMobile()

  const navigate = useNavigate();

  const { user } = useAuth()

  const queryParams = useQueryParams();

  // const [openDialog, setOpenDialog] = useState(false)
  const [curDate, setCurDate] = useState({
    day: today?.day,
    month: today?.month,
    year: today?.year
  })
  const [dateDisplay, setDateDisplay] = useState('')

  const [activeView, setActiveView] = useState('timeline')

  const formatDisplay = (month, year, date) => {
    let displayDate = ''
    if (activeView === 'month') {
      displayDate = `${monthsObj[month].month} ${year}`
    } else {
      displayDate = `${monthsObj[month].month} ${date}, ${year}`
    }
    setDateDisplay(displayDate)
  }

  const updateUrlDate = ({day, month, year}) => {
    let dateStr = `${month + 1}/${day}/${year}`
    let curView = queryParams?.view || "timeline"
    navigate(`?view=${curView}&date=${dateStr}`);
  }

  const updateUrlView = (view) => {
    let curView = view || "timeline"
    let curDateStr = `${curDate.month + 1}/${curDate.day}/${curDate.year}`
    navigate(`?view=${curView}&date=${curDateStr}`);
  }

  const move = (dir) => {
    dir = dir === "right" ? "add" : "sub"
    
    if (activeView === 'month') {
      let {month, year} = calculateMonthAndYear(curDate.month, curDate.year, dir)
      updateUrlDate({day: curDate.day, month, year})
    } else if (activeView === 'week') {
      let newDate = calculateWeeks(constructDateFromObj(curDate), 1, dir);
      let {day, month, year} = getDateParts(newDate)
      updateUrlDate({day, month, year})
    } else {
      let newDate = calculateDate(constructDateFromObj(curDate), 1, dir)
      let {day, month, year} = getDateParts(newDate)
      updateUrlDate({day, month, year})
    }
  }

  /* load stylists */
  const setActiveStylists = useStylistsStore((state) => state.setActiveStylists);
  const setTempActiveStylists = useStylistsStore((state) => state.setTempActiveStylists);

  /* load stylist for local view */

  const localActiveStylists = useLocalStylistsStore((state) => state.activeStylists);
  const setLocalActiveStylists = useLocalStylistsStore((state) => state.setActiveStylists);
  const setLocalTempActiveStylists = useLocalStylistsStore((state) => state.setTempActiveStylists);

  const { data: stylists } = useQuery({
    queryKey: ['stylists'],
    queryFn: () => getStylists(),
  })

  const { data: stylistView } = useQuery({
    queryKey: ["settings", "calendar", "stylist_view"],
    queryFn: () => getStylistView(),
    // staleTime: Infinity,
    // refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (stylists?.length) {
      const svData = stylistView?.data ?? [];
      const savedIds = new Set(svData.map((sv) => sv.stylist_id));
      const byId = new Map(stylists.map((el) => [el.stylist_id, el]));
  
      const decorate = (el) => {
        const sv = svData.find((sv) => sv.stylist_id === el.stylist_id);
        const checked = user?.stylist_id ? user?.stylist_id === el.stylist_id ? true : false : false;
        return {
          ...el,
          checked: sv && sv?.checked ? true : checked,
        };
      };
  
      // 1) stylists that exist in the saved view, in the saved order
      //    (byId lookup drops any saved stylist that no longer exists in the master list)
      const ordered = svData
        .map((sv) => byId.get(sv.stylist_id))
        .filter(Boolean)
        .map(decorate);
  
      // 2) stylists not yet in the saved view, appended in their natural order
      const remaining = stylists
        .filter((el) => !savedIds.has(el.stylist_id))
        .map(decorate);
  
      let activeStylists = [...ordered, ...remaining];
  
      let findOne = activeStylists.find((el) => el.checked);
      if (!findOne && activeStylists.length) activeStylists[0].checked = true;
  
      let tempActiveStylists = activeStylists.map((el) => ({ ...el }));
      setActiveStylists(activeStylists);
      setTempActiveStylists(tempActiveStylists);
    }
  }, [stylists, stylistView])


  // local stylists, NEW!!
  useEffect(() => {
    if (stylists?.length) {
      if (localActiveStylists?.length) return
      let activeStylists = stylists.map((el, i) => {
        let checked = user?.stylist_id ? user?.stylist_id === el.stylist_id ? true : false : false
        return {
          ...el,
          checked: checked
        }
      })
      let findOne = activeStylists.find((el) => el.checked);
      if (!findOne) activeStylists[0].checked = true;
      let tempActiveStylists = [...activeStylists.map((el) => ({...el}))];
      setLocalActiveStylists(activeStylists);
      setLocalTempActiveStylists(tempActiveStylists);
    }
  }, [stylists])

  /* end load stylists */


  useEffect(() => {
    formatDisplay(curDate.month, curDate.year, curDate.day)
  }, [activeView, curDate])


  // get date and view from url
  useLayoutEffect(() => {
    setActiveView(queryParams?.view || "timeline")
    if (queryParams?.date) {
      let fullDate = getDateParts(queryParams.date)
      setCurDate(fullDate)
    } else {
      let today = getToday()
      setCurDate({
        day: today?.day,
        month: today?.month,
        year: today?.year
      })
    }
  }, [queryParams?.view, queryParams?.date])


  const { data: setting } = useQuery({
    queryKey: ['settings/calendar', 'general'],
    queryFn: () => getSettingByResource('calendar', 'general'),
    staleTime: Infinity
  })

  useLayoutEffect(() => {
    if (setting) {
      let data = setting.data
      let location = locations?.[data.location]
      let tzeis = data?.tzeis?.replace(/\D/g, "") || 72;
      let candleLighting = data?.candle_lighting?.replace(/\D/g, "") || 15;
      if (location) {
        location = new Location(...Object.values(location))
      }
      let options = {
        location: location ? location : Location.lookup("New York"),
        sedrot: data.display_parshios || false,
        candlelighting: data.display_zmanim || false,
        candleLightingMins: candleLighting,
        havdalahMins: +tzeis
      }
      setCalOptions(options)
    }
  }, [setting])

  return (
    <CalendarContext.Provider value={{setting, calOptions}}>
      <FollowUpSnackbar />
    <Container>
      <FormDialog size="xl" title="Schedule Appointment" forceOpen={openDialog} forceClose={() => setOpenDialog(false)} text="" saveText="Add New Appointment" saveFunc={() => {}} useForm={true} fullScreen>
        <EditSchedule dialog={true} event={event} />
      </FormDialog>
    {/* {isMobile && <Header><h2>Calendar</h2></Header>} */}
      <Flex dir={isMobile ? "column" : "row"} gap={isMobile ? "1rem" : "0"}>
      { !isTablet ? <Tag dir="row" justify="center" align="center"><CalendarIcon color={theme.colors.primary} /> Calendar</Tag> : null }
        <ActionContainer dir={isMobile ? "column" : "row"}>
          <SelectDateContainer justify="flex-start">
          <SelectDate />
          <DateSelector dir="row">
            <Arrow onClick={() => move("left")}><LeftArrow /></Arrow>
            <DateText>{dateDisplay}</DateText>
            <Arrow onClick={() => move("right")}><RightArrow /></Arrow>
          </DateSelector>

            {isMobile ?<FormControl size="small" sx={{ bgcolor: "#fff", height: "3rem", fontSize: "1.2rem" }}>
              {/* <InputLabel>Resource</InputLabel> */}
              <Select value={queryParams?.view || "timeline"} onChange={(e) => updateUrlView(e.target.value)} sx={{height: "3rem", fontSize: "1.2rem"}}>
                <MenuItem value='timeline' dense >
                  Day
                </MenuItem>
                <MenuItem value='week' dense>
                  Week
                </MenuItem>
              </Select>
            </FormControl> : null}
            {isMobile && activeView !== "timeline" ? <SelectStylistLocal /> : null}

          </SelectDateContainer>

          {!isMobile ? <View dir="row" justify="space-around">
            <ViewBlock onClick={() => updateUrlView('timeline')}  $active={activeView === "timeline"}>Day</ViewBlock>
            <ViewBlock onClick={() => updateUrlView('3day')}  $active={activeView === "3day"}>3 Days</ViewBlock>
          <ViewBlock onClick={() => updateUrlView('week')} $active={activeView === "week"}>Week</ViewBlock>
          <ViewBlock onClick={() => updateUrlView('list')} $active={activeView === "list"}>List</ViewBlock>
          <ViewBlock onClick={() => updateUrlView('month')} $active={activeView === "month"}>Month</ViewBlock>
          </View> : null}

          {!isMobile && activeView !== "timeline" ? <SelectStylistLocal /> : null}
        </ActionContainer>
      </Flex>

      {activeView === 'month' && <Month year={curDate?.year} createEvent={createNewEvent} month={curDate?.month} />}
      {activeView === 'timeline' ? <TimelineComponent createEvent={createNewEvent} curDate={curDate} /> : null}
      {activeView === 'week' && <WeekComponent createEvent={createNewEvent} curDate={curDate} />}
      {activeView === '3day' && <ThreeDayComponent createEvent={createNewEvent} curDate={curDate} />}
      {activeView === 'list' && <ListView createEvent={createNewEvent} curDate={curDate} />}
      {/* {activeView === 'day' && <DayComponent createEvent={createNewEvent} curDate={curDate} />} */}
    </Container>
    </CalendarContext.Provider>
  )
}

export default CalendarProvider;